import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable, NgZone, signal } from "@angular/core";
import { Subscription, timer } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: 'root' })
export class ConnectionLostBarService {
 
     /** true => a barra deve estar visível */
    readonly offline = signal(false);
 
    /** Causa PROVÁVEL da falha (heurística, não é certeza). */
    readonly reason = signal<'local' | 'server' | null>(null);
 
    /** Endpoint leve da SUA api para testar a conexão. Ajuste para a sua rota. */
    private readonly healthUrl = environment.endpoint+'SystemServices/health';
 
    /** Endpoint externo de altíssima disponibilidade p/ sondar a internet. */
    private readonly probeUrl = 'https://www.google.com/generate_204';
 
    /** Backoff entre pings (ms): 2s, 4s, 8s e depois estabiliza em 10s. */
    private readonly intervals = [2000, 4000, 8000, 10000];
    private attempt = 0;
    private pollSub?: Subscription;
 
    /** HttpClient isolado: NÃO passa pelos interceptors (sem recursão no handler). */
    private readonly http: HttpClient;
 
    constructor(private zone: NgZone, backend: HttpBackend) {
        this.http = new HttpClient(backend);
 
        window.addEventListener('online', () => this.checkNow());
        window.addEventListener('offline', () => this.show());
        document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
 
    /** Perda de conexão detectada (chamado pelo interceptor/handler com status 0). */
    show(): void {
        this.setOffline(true);
        this.classifyCause();   // diagnostica logo de cara
        this.startPolling();
    }
 
    /** Reconectado: esconde a barra, limpa a causa e encerra o heartbeat. */
    hide(): void {
        this.stopPolling();
        this.zone.run(() => {
            this.offline.set(false);
            this.reason.set(null);
        });
    }
 
    /** Força uma verificação imediata (voltar 'online' ou aba voltar ao foco). */
    checkNow(): void {
        if (!this.offline()) {
            return;
        }
        this.pollSub?.unsubscribe();
        this.pollSub = undefined;
        this.attempt = 0;
        this.ping();
    }
 
    private onVisibilityChange(): void {
        if (document.hidden) {
            this.pausePolling();
        } else if (this.offline()) {
            this.checkNow();
        }
    }
 
    private startPolling(): void {
        if (this.pollSub) {
            return;
        }
        this.attempt = 0;
        this.scheduleNext();
    }
 
    private stopPolling(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = undefined;
        this.attempt = 0;
    }
 
    private pausePolling(): void {
        this.pollSub?.unsubscribe();
        this.pollSub = undefined;
    }
 
    private scheduleNext(): void {
        if (document.hidden) {
            this.pollSub = undefined;
            return;
        }
        const delay = this.intervals[Math.min(this.attempt, this.intervals.length - 1)];
        this.pollSub = timer(delay).subscribe(() => this.ping());
    }
 
    private ping(): void {
        // this.http.head(this.healthUrl, { observe: 'response', 
        //             headers: {contentType: 'application/json'} }
        //     ).subscribe({
        //     next: () => this.zone.run(() => this.hide()),   // servidor respondeu: reconectado
        //     error: () => {                                  // ainda fora: re-diagnostica e re-tenta
        //         this.classifyCause();
        //         this.attempt++;
        //         this.scheduleNext();
        //     },
        // });

        
 
        // HEAD é mais leve; se o servidor não responder a HEAD, troque por this.http.get(...).
        this.http.head(this.healthUrl, { observe: 'response',headers:{'Content-Type': 'application/json'} }).subscribe({
            next: () => this.zone.run(() => this.hide()),  // voltou: esconde + para
            error: () => {                                  // ainda fora: tenta de novo
                this.classifyCause();
                this.attempt++;
                this.scheduleNext();
            },
        });

    }
 
    /** Decide se a causa provável é 'local' (internet) ou 'server' (api fora). */
    private classifyCause(): void {
        this.hasInternet().then(online => {
            this.zone.run(() => this.reason.set(online ? 'server' : 'local'));
        });
    }
 
    /**
     * Heurística: tem internet?
     *  - navigator.onLine === false  -> definitivamente NÃO.
     *  - senão sonda um host externo com fetch no-cors (resposta opaca, mas se
     *    a requisição completar, a internet está OK). Erro de rede => rejeita.
     */
    private async hasInternet(): Promise<boolean> {
        if (!navigator.onLine) {
            return false;
        }
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 3000);
            await fetch(this.probeUrl, {
                mode: 'no-cors',
                cache: 'no-store',
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return true;
        } catch {
            return false; // network error ou timeout -> sem internet (ou bloqueio)
        }
    }
 
    private setOffline(value: boolean): void {
        this.zone.run(() => this.offline.set(value));
    }
}
